import React from "react";
import { Autocomplete, Checkbox, Chip, TextField, Typography } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

// ─── Types ────────────────────────────────────────────────────────────────────

type DropdownOption = string | { value: string; label: string; name?: string };

interface FilterDropdownAutocompleteProps {

    value: string | string[];
    options: DropdownOption[];
    isMultiSelect: boolean;
    className?: string;
    inputLabel?: string;
    touched?: boolean;
    error?: string;
    alternateNoneLabel?: string;
    onChange: (value: string | string[]) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Extracts a plain string key from an option.
 * Object options use their `.value` property; strings are returned as-is.
 */
function getOptionKey(option: DropdownOption): string {
    return typeof option === "object" && option !== null
        ? String(option.value)
        : String(option);
}

/**
 * Returns the human-readable display label for an option.
 * Resolves string IDs back to labels using the full options list when needed.
 */
function resolveLabel(
    option: DropdownOption,
    allOptions: DropdownOption[],
    alternateNoneLabel?: string
): string {
    const noneLabel = alternateNoneLabel || "--None--";

    // Object option — label is already present
    if (typeof option === "object" && option !== null) {
        return option.label === "none" ? noneLabel : option.label;
    }

    // Plain string — might be a stored ID needing resolution
    if (
        Array.isArray(allOptions) &&
        allOptions.length > 0 &&
        typeof allOptions[0] === "object"
    ) {
        const match = allOptions.find(
            (d: any) => String(d.value) === String(option)
        ) as any;
        if (match) return match.label === "none" ? noneLabel : match.label;
    }

    const strOption = option as string;
    return strOption === "none" ? noneLabel : strOption;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * A reusable Autocomplete for Advanced Filter dropdowns.
 *
 * - Single-select: standard Autocomplete with no checkboxes.
 * - Multi-select:  checkboxes on each option, chips (first 2) + "+N" overflow
 *   for selected tags — matching the Payer Scorecard design.
 *
 * Handles both plain-string option arrays (isDropdown fields) and
 * {value, label} object arrays (isStrictDropdown + isMultiSelection fields).
 */
export function FilterDropdownAutocomplete({
    value,
    options,
    isMultiSelect,
    className,
    inputLabel,
    touched,
    error,
    alternateNoneLabel,
    onChange,
}: FilterDropdownAutocompleteProps) {

    // ── Normalise: extract .value string from object options ──────────────────
    function handleChange(_e: React.SyntheticEvent, selected: any) {
        if (isMultiSelect && Array.isArray(selected)) {
            const normalized: string[] = selected.map((v) =>
                typeof v === "object" && v !== null ? v.value : v
            );

            // 'none' is the placeholder — keep it exclusive
            if (normalized.includes("none") && !(value as string[])?.includes("none")) {
                onChange(["none"]);
            } else if (normalized.includes("none") && normalized.length > 1) {
                onChange(normalized.filter((v) => v !== "none"));
            } else {
                onChange(normalized);
            }
        } else {
            // Single-select: normalise object → value string
            const normalised =
                typeof selected === "object" && selected !== null
                    ? selected.value
                    : selected;
            onChange(normalised);
        }
    }

    // ── getOptionLabel ────────────────────────────────────────────────────────
    function getOptionLabel(option: DropdownOption): string {
        if (option === null || option === undefined) return "";
        return resolveLabel(option, options, alternateNoneLabel);
    }

    // ── isOptionEqualToValue ──────────────────────────────────────────────────
    function isOptionEqualToValue(option: DropdownOption, val: DropdownOption): boolean {
        if (option === null || val === null) return option === val;
        return getOptionKey(option) === getOptionKey(val as DropdownOption);
    }

    // ── renderOption (with checkbox for multi-select) ─────────────────────────
    function renderOption(
        props: React.HTMLAttributes<HTMLLIElement>,
        option: DropdownOption,
        { selected }: { selected: boolean }
    ) {
        const label = resolveLabel(option, options, alternateNoneLabel);
        const key = getOptionKey(option);
        return (
            <li {...props} key={key}>
                {isMultiSelect && (
                    <Checkbox
                        icon={checkboxIcon}
                        checkedIcon={checkboxCheckedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                    />
                )}
                {label}
            </li>
        );
    }

    // ── renderTags — chips (first 2) + "+N" overflow ──────────────────────────
    function renderTags(
        tagValue: DropdownOption[],
        getTagProps: (params: { index: number }) => object
    ) {
        const numTags = tagValue.length;
        return (
            <Typography
                variant="caption"
                component="span"
                style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 4,
                }}
            >
                {tagValue.slice(0, 2).map((option, index) => (
                    <Chip
                        size="small"
                        color="secondary"
                        key={index}
                        label={resolveLabel(option, options, alternateNoneLabel)}
                        {...getTagProps({ index })}
                        style={{ maxWidth: 140, overflow: 'hidden' }}
                        sx={{
                            '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                            }
                        }}
                    />
                ))}
                {numTags > 2 && (
                    <span
                        style={{
                            whiteSpace: "nowrap",
                            fontSize: "0.75rem",
                            color: "rgba(0,0,0,0.6)",
                        }}
                    >
                        +{numTags - 2}
                    </span>
                )}
            </Typography>
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Autocomplete
            multiple={isMultiSelect}
            disableCloseOnSelect={isMultiSelect}
            className={className}
            value={isMultiSelect ? ((value && !Array.isArray(value)) ? [value]:value as string[]) || [] : value}
            options={options}
            onChange={handleChange}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            renderOption={renderOption}
            renderTags={renderTags}
            renderInput={(params) => (
                <TextField
                    {...params}
                    error={Boolean(touched && error)}
                    helperText={touched && error}
                    label={inputLabel ?? (isMultiSelect ? "Select Options (multi)" : "Select Option")}
                />
            )}
        />
    );
}
